import { useEffect, useState } from 'react';
import {
  Box, Stack, Typography, Chip, Divider, Accordion,
  AccordionSummary, AccordionDetails, Skeleton, Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useLazyGetTatMerchantContextQuery, useLazyGetTatConfigQuery } from '../../../redux/tatApiSlice';
import { useGetAllocationMerchantDetailQuery, useGetAllocationRulesQuery } from '../../../redux/allocationApiSlice';

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

function KV({ label, value, mono }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: 'text.disabled', fontSize: 9.5, display: 'block' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600, mt: 0.2,
          fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit',
          fontSize: mono ? 11.5 : 13,
          wordBreak: 'break-word',
        }}
      >
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

function SectionHeader({ icon, title, badge, badgeColor = 'primary', status }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Typography sx={{ fontSize: 18, lineHeight: 1 }}>{icon}</Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      {badge && (
        <Chip size="small" label={badge} color={badgeColor} sx={{ fontWeight: 700, fontSize: 10.5, height: 20 }} />
      )}
      {status != null && (
        status
          ? <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
          : <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
      )}
    </Stack>
  );
}

function GridKV({ items }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 2, mt: 1 }}>
      {items.map(([label, value, mono]) => (
        <KV key={label} label={label} value={value} mono={mono} />
      ))}
    </Box>
  );
}

function LoadingGrid() {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 2, mt: 1 }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <Box key={i}>
          <Skeleton variant="text" width={60} height={12} />
          <Skeleton variant="text" width={120} height={18} />
        </Box>
      ))}
    </Box>
  );
}

// ── Pricing Setup section ─────────────────────────────────────────────────

function PricingSection({ data }) {
  if (!data) return <Alert severity="info" sx={{ fontSize: 12 }}>Pricing setup not started yet.</Alert>;

  const freightDirs = (data.freightDirections || []).join(', ') || '—';
  const serviceTypes = (data.resolvedServiceTypes || []).join(', ') || '—';

  return (
    <Box>
      <GridKV items={[
        ['Merchant Code', data.merchantId, true],
        ['Status', data.status],
        ['Company Name', data.companyName || data.name],
        ['Email', data.email],
        ['Contact', data.contact_no],
        ['City / State', [data.city, data.state].filter(Boolean).join(', ') || '—'],
        ['Freight Directions', freightDirs],
        ['Payment Mode', data.paymentMode],
        ['GST Stance', data.gstStance],
        ['Weight Ceiling', data.weightCeiling ? `${data.weightCeiling / 1000} kg` : '—'],
        ['Invoice Ceiling', data.invoiceCeiling ? `₹${Number(data.invoiceCeiling).toLocaleString('en-IN')}` : '—'],
        ['Volumetric Coeff', data.volumetricCoefficient],
      ]} />

      {serviceTypes !== '—' && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: 'text.disabled', fontSize: 9.5 }}>
            Resolved Service Types
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
            {(data.resolvedServiceTypes || []).map((st) => (
              <Chip key={st} size="small" label={st} sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, height: 20, fontWeight: 600, bgcolor: (t) => alpha(t.palette.primary.main, 0.08), color: 'primary.dark' }} />
            ))}
          </Stack>
        </Box>
      )}

      {data.globalRto && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: 'text.disabled', fontSize: 9.5 }}>
            RTO Configuration
          </Typography>
          <GridKV items={[
            ['Mode', data.globalRto?.mode],
            ['Amount / %', data.globalRto?.fixed_amount != null ? `₹${data.globalRto.fixed_amount}` : data.globalRto?.pct_fwd != null ? `${data.globalRto.pct_fwd}% of fwd` : '—'],
          ]} />
        </Box>
      )}
    </Box>
  );
}

// ── TAT Setup section ─────────────────────────────────────────────────────

function TatSection({ merchantId }) {
  const [getCtx, { data: ctxRaw, isLoading: ctxLoading }] = useLazyGetTatMerchantContextQuery();
  const [getCfg, { data: cfgRaw, isLoading: cfgLoading }] = useLazyGetTatConfigQuery();

  useEffect(() => {
    if (merchantId) { getCtx(merchantId); getCfg(merchantId); }
  }, [merchantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const ctx = ctxRaw?.data ?? ctxRaw;
  const cfg = cfgRaw?.data ?? cfgRaw;
  const isLoading = ctxLoading || cfgLoading;

  if (isLoading) return <LoadingGrid />;
  if (!ctx && !cfg) return <Alert severity="info" sx={{ fontSize: 12 }}>TAT setup not started yet.</Alert>;

  const selectedSTs = ctx?.active_service_types || cfg?.activeServiceTypes || [];
  const tatStatus = ctx?.tat_status || '—';

  return (
    <Box>
      <GridKV items={[
        ['TAT Status', tatStatus],
        ['Merchant Name', ctx?.merchantName || '—'],
        ['File Count', ctx?.file_count ?? '—'],
        ['Published At', fmtDate(ctx?.published_at)],
        ['Last Updated', fmtDate(ctx?.last_updated)],
        ['Published By', ctx?.publishedBy || '—'],
      ]} />

      {selectedSTs.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: 'text.disabled', fontSize: 9.5 }}>
            Configured Service Types
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
            {selectedSTs.map((st) => (
              <Chip key={st} size="small" label={st} sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, height: 20, fontWeight: 600, bgcolor: (t) => alpha(t.palette.success.main, 0.08), color: 'success.dark' }} />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

// ── Allocation section ────────────────────────────────────────────────────

function AllocationSection({ merchantId }) {
  const { data: detailRaw, isLoading: detailLoading } = useGetAllocationMerchantDetailQuery(merchantId, { skip: !merchantId });
  const { data: rulesRaw, isLoading: rulesLoading } = useGetAllocationRulesQuery(merchantId, { skip: !merchantId });

  const detail = detailRaw?.data ?? detailRaw;
  const rules = rulesRaw?.data ?? rulesRaw;
  const isLoading = detailLoading || rulesLoading;

  if (isLoading) return <LoadingGrid />;
  if (!detail && !rules) return <Alert severity="info" sx={{ fontSize: 12 }}>Allocation not configured yet.</Alert>;

  const customRules = rules?.customRules || [];
  const reverseRules = rules?.reverseRules || [];

  return (
    <Box>
      <GridKV items={[
        ['Allocation Version', detail?.allocationVersion || '—'],
        ['Allocation Mode', detail?.allocationMode || '—'],
        ['Rule Count', detail?.allocationRuleCount ?? '—'],
        ['Published By', detail?.allocationPublishedBy || '—'],
        ['Effective From', fmtDate(detail?.allocationEffFrom)],
        ['Pickup Pincodes', (detail?.pickupPincodes || []).join(', ') || '—'],
        ['Tier', detail?.tier || '—'],
        ['RVP Enabled', detail?.rvpEnabled != null ? (detail.rvpEnabled ? 'Yes' : 'No') : '—'],
        ['Payment Modes', (detail?.paymentModes || []).join(', ') || '—'],
        ['Freight Directions', (detail?.contractDirections || []).join(', ') || '—'],
      ]} />

      {(customRules.length > 0 || reverseRules.length > 0) && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2}>
            <Chip size="small" label={`${customRules.length} forward rule${customRules.length !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ fontWeight: 700, fontSize: 11, height: 22 }} />
            {reverseRules.length > 0 && (
              <Chip size="small" label={`${reverseRules.length} reverse rule${reverseRules.length !== 1 ? 's' : ''}`} color="secondary" variant="outlined" sx={{ fontWeight: 700, fontSize: 11, height: 22 }} />
            )}
            <Chip size="small" label={rules?.source === 'draft' ? 'Draft' : 'Published'} color={rules?.source === 'draft' ? 'warning' : 'success'} sx={{ fontWeight: 700, fontSize: 11, height: 22 }} />
          </Stack>
        </Box>
      )}
    </Box>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────

export default function OnboardingTab({ data, id }) {
  const pricingDone = !!(data?.status && data.status !== 'CREATED');
  const allocationVersion = null; // loaded inside AllocationSection

  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
        All data captured across Pricing Setup, TAT Setup, and D2C Allocation for this merchant.
      </Typography>

      <Stack spacing={1.5}>

        {/* ── Pricing Setup ── */}
        <Accordion defaultExpanded elevation={0} variant="outlined" sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 52, '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
            <SectionHeader
              icon="💰"
              title="Pricing Setup"
              badge={data?.status || 'Not Started'}
              badgeColor={pricingDone ? 'success' : 'warning'}
              status={pricingDone}
            />
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <PricingSection data={data} />
          </AccordionDetails>
        </Accordion>

        {/* ── TAT Setup ── */}
        <Accordion defaultExpanded elevation={0} variant="outlined" sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 52, '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
            <SectionHeader icon="⏱" title="TAT Setup" />
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <TatSection merchantId={id} />
          </AccordionDetails>
        </Accordion>

        {/* ── D2C Allocation ── */}
        <Accordion defaultExpanded elevation={0} variant="outlined" sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 52, '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
            <SectionHeader icon="📦" title="D2C Allocation" />
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <AllocationSection merchantId={id} />
          </AccordionDetails>
        </Accordion>

      </Stack>
    </Box>
  );
}
